import { useCallback } from 'react';
import { timeFriday } from 'd3-time';

import formatDate from 'helpers/formatDate';
import { MarginGroup } from 'components/data-visualization/BubbleChart/MarginGroup';
import useMedia from 'hooks/useMedia';
import getYTicks from './utils/getYTicks';

import { TickLine, Tick } from './shared.style';
import { func, number, shape } from 'prop-types';
import formatNumber from '../../../helpers/formatNumber';

export default Axes;

/**
 * Axes renders the horizontal and vertical axes and their tick lines.
 *
 * @param {object} props React props
 * @param {number} props.height The height of the drawing area
 * @param {object} props.margin Margins as per d3.js conventions
 * @param {function} props.x The scale for the horizontal (x) axis
 * @param {function} props.y The scale for the vertical (y) axis
 */
function Axes(props) {
  const { x, y, height, margin } = props;

  /**
   * Local variables
   */
  const isSmallScreen = useMedia('(max-width: 40em)');

  /**
   * Get the ticks for the horizontal (x) axis.
   * Show a tick only every two weeks on smaller screens
   */
  const xTickLines = x.ticks(timeFriday);
  const xTickLabels = x.ticks(isSmallScreen ? timeFriday.every(2) : timeFriday);

  /**
   * Get the ticks for the vertical (y) axis.
   */
  const yTicks = useCallback(getYTicks(y), [y]);

  return (
    <>
      <MarginGroup margin={margin}>
        {xTickLines.map((tick) => (
          <TickLine
            key={`tick-line-${tick}`}
            x1={x(tick)}
            x2={x(tick)}
            y2={height}
            isDashed
          />
        ))}

        {yTicks.filter(Boolean).map((tick) => (
          <TickLine
            key={`tick-line-${tick}`}
            x1={x.range()[0]}
            x2={x.range()[1]}
            y1={y(tick)}
            y2={y(tick)}
            isDashed
          />
        ))}

        <TickLine x2={x.range()[1]} y1={y(0)} y2={y(0)} />

        <Tick y={y(yTicks[yTicks.length - 1])} dx={4} dy="1.15em">
          {formatNumber(yTicks[yTicks.length - 1], 'number', true)}
        </Tick>

        <Tick y={y(0)} dx={4} dy="-0.35em">
          0
        </Tick>
      </MarginGroup>

      <MarginGroup margin={{ ...margin, top: margin.top + height }}>
        {xTickLabels.map((tick) => {
          const byTimeStamp = (el) => el.getTime() === tick.getTime();
          const tickLineIdx = xTickLines.findIndex(byTimeStamp);

          const isFirstTick = tickLineIdx === 0;
          const isLastTick = tickLineIdx === xTickLines.length - 1;

          const alignment = isFirstTick
            ? 'start'
            : isLastTick
            ? 'end'
            : 'middle';

          return (
            <Tick key={tick} x={x(tick)} dy="1.15em" textAnchor={alignment}>
              {formatDate(tick, 'day', true)}
            </Tick>
          );
        })}
      </MarginGroup>
    </>
  );
}

Axes.propTypes = {
  x: func.isRequired,
  y: func.isRequired,
  height: number.isRequired,
  margin: shape({
    top: number.isRequired,
    right: number.isRequired,
    bottom: number.isRequired,
    left: number.isRequired,
  }).isRequired,
};
